import { IFilterState } from '../contexts/FilterContext';
import fetchData from './fetchData';

// Helper function to format dates for API calls without timezone conversion
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface PlxOrder {
  mandatorid: string;
  kunde: number | null;
  vertrags_seqnr: number | null;
  created_on: string;
  ordernumber: string;
  artikel: string | null;
  werbeaktion_wbkz: string | null;
  vertrag_sts: string | null;
  kuendigungsdatum: string | null;
  aboende_datum: string | null;
  gender: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  firma: string | null;
  strasse: string | null;
  strasse_postleitzahl_neu: string | null;
  ort: string | null;
  email: string | null;
  vatid: string | null;
  postofficebox: string | null;
  countryisocode: string | null;
  created_by: string | null;
  status: string;
  offerid: string | null;
  offerurl: string | null;
  banner: string | null;
  product_name: string;
  quarterly_price: number;
  is_upsell: number;
  is_one_time: number;
  trial_days: number;
  subscription_lifetime_days: number | null;
  total_revenue_generated: number | null;
  actual_revenue: number | null;
  revenue_variance: number | null;
  has_actual_data: boolean | null;
  umsatz_brutto: number | null;
  umsatz_netto: number | null;
  zahlung_brutto: number | null;
  zahlung_netto: number | null;
  storno_brutto: number | null;
  storno_netto: number | null;
  ausbuchung_brutto: number | null;
  ausbuchung_netto: number | null;
  rueckueberweisung_brutto: number | null;
  rueckueberweisung_netto: number | null;
  ruecklastschrift_brutto: number | null;
  ruecklastschrift_netto: number | null;
  invoices_paid: number | null;
  display_price: number;
  days_since_order: number;
  subscription_type: string | null;
  source: string;
}

export interface PlxPaymentEvent {
  payment_number: number;
  payment_date: string;
  payment_amount: number;
  subscription_type: string;
  is_past: boolean;
  actually_paid: boolean;
}

export interface PlxOrderDetails {
  order: PlxOrder;
  paymentEvents: PlxPaymentEvent[];
}

export interface PlxOrderStats {
  total_orders: number;
  accepted_orders: number;
  rejected: number;
  active_orders: number;
  cancelled: number;
  ended: number;
  trial_orders: number;
  trial_ended_unpaid: number;
  paid_subscriptions: number;
  paid_quarterly: number;
  paid_annual: number;
  upsell_subscriptions: number;
  one_time_orders: number;
  total_revenue: number;
  conversion_rate: number;
  churn_rate: number;
  rejection_rate: number;
  active_retention_rate: number;
  average_quarterly_price: number;
  average_order_value: number;
  average_lifetime_value: number;
  upsell_rate: number;
  paid_upsell_rate: number;
  accepted_unpaid_orders: number;
  active_unpaid_orders: number;
  abo_cash_30_days_count: number;
  abo_2_60_days_count: number;
  abo_4_120_days_count: number;
  abo_cash_30_days_pct: number;
  abo_2_60_days_pct: number;
  abo_4_120_days_pct: number;
  abo_cash_30_days_quarterly_pct: number;
  abo_cash_30_days_upsell_pct: number;
  abo_2_60_days_quarterly_pct: number;
  abo_2_60_days_upsell_pct: number;
  abo_4_120_days_quarterly_pct: number;
  abo_4_120_days_upsell_pct: number;
  active_trial_orders: number;
  active_paid_quarterly: number;
  active_paid_annual: number;
  active_one_time_orders: number;
  accepted_trial_orders: number;
  accepted_paid_quarterly: number;
  accepted_paid_annual: number;
  accepted_one_time_orders: number;
  total_30d_retention: number;
  quarterly_30d_retention: number;
  annual_30d_retention: number;
  quarterly_60d_retention: number;
  annual_60d_retention: number;
  quarterly_120d_retention: number;
  annual_120d_retention: number;
  avg_current_lifetime_days: number;
  total_upsells: number;
  upsells_paid_year_1: number;
  upsells_unpaid_year_1: number;
  upsells_paid_year_2: number;
  upsell_year_1_payment_rate: number;
  upsell_year_2_from_total_rate: number;
  upsell_year_2_from_payers_rate: number;
  expected_total_revenue: number;
  avg_anfo_wert: number;
}

export interface PlxWerbeaktionConfig {
  werbeaktion_wbkz: string;
  plx_product: string;
  product_name: string;
  description?: string;
  quarterly_price: number;
  is_upsell: boolean;
  is_one_time: boolean;
  is_cash_upfront: boolean;
  trial_days: number;
  created_at: string;
  updated_at: string;
}

export interface PlxProduct {
  id: number;
  plx_product: string;
  name: string;
  is_one_time?: boolean;
  description?: string;
  /** Vom Wochen-Cron gepflegt, nur fuer die Cashflow-Prognose. */
  expected_conversion_rate_upsell?: number;
  expected_conversion_rate_quarterly?: number;
  default_quarterly_price?: number;
  /** Nur lesen — Schreiber ist ausschliesslich der AnfoWertService. */
  anfo_wert?: number;
  anfo_cpa?: number;
}

export interface PlxWerbeaktionConfigHistory {
  id: number;
  werbeaktion_wbkz: string;
  plx_product: string;
  product_name: string;
  quarterly_price: number;
  is_upsell: boolean;
  is_one_time: boolean;
  is_cash_upfront: boolean;
  trial_days: number;
  valid_from: string;
  valid_until: string | null;
  created_at: string;
  created_by: string;
}

export interface UnmappedWKZ {
  werbeaktion_wbkz: string;
  order_count: number;
  distinct_products: number;
  product_codes: string;
  product_names: string;
  first_order_date: string;
  last_order_date: string;
}

export interface PlxFilterOptions {
  werbeaktionen: string[];
  artikel: { artikel: string; name: string }[];
  banner: string[];
  /** Quellen = Kanal-Labels aus node-app/src/helpers/orderChannel.ts, in Anzeige-Reihenfolge. */
  sources: string[];
}

/** Seitenfilter der Bestellliste als Query-Parameter — dieselben, die `/stats` und die Kacheln bekommen. */
const plxListFilterOptions = (filterState: IFilterState): Record<string, any> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters for PLX
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return options;
};

/**
 * Eine Zeile der Bestellliste v2 (`GET /api/plx-orders/list`, `node-app/src/helpers/plxOrderListSql.ts`).
 * Ohne Adressdaten — die stehen nur im Bestell-Detail. Datumswerte sind Berliner Wallclock-Strings.
 */
export interface PlxOrderListRow {
  ordernumber: string;
  mandatorid: string;
  /** `YYYY-MM-DD HH:MM:SS`, Berlin */
  created_on: string;
  kunde: number | null;
  email: string | null;
  vorname: string | null;
  nachname: string | null;
  firma: string | null;
  artikel: string | null;
  werbeaktion_wbkz: string | null;
  vertrag_sts: string | null;
  kuendigungsdatum: string | null;
  aboende_datum: string | null;
  offerurl: string | null;
  product_name: string;
  is_upsell: boolean;
  is_one_time: boolean;
  trial_days: number;
  trial_end: string | null;
  /** Quartalspreis, beim Jahresbezug das Vierfache, bei Einmalkauf der Einmalpreis (brutto). */
  display_price: number;
  /** Bezahlt bis heute, brutto (`plx_order_metrics.actual_revenue`). */
  actual_revenue: number;
  /** Bisher bezahlt NETTO (`zahlung_netto`) — nur an Anfos, sonst null (P5, Sektion /auswertungen/bestellungen). */
  paid_revenue_net: number | null;
  has_actual_data: boolean;
  zahlung_brutto: number | null;
  storno_brutto: number;
  ruecklastschrift_brutto: number;
  rueckueberweisung_brutto: number;
  ausbuchung_brutto: number;
  subscription_type: string | null;
  /** Kanal-Label (`helpers/orderChannel.ts`). */
  source: string;
  blog: string | null;
  /** Kanal-Schlüssel (`orderChannelCaseSql`) und -Label der Sektion — wie die Übersicht „Je Kanal" (P5-Review). */
  channel: string | null;
  channel_label: string | null;
  /** Blog wie die Übersicht „Je Blog" (`blogDimensionSql`, mit „Kennung ohne Marke" / „Ohne Kennung"). */
  blog_sektion: string | null;
  effective_banner: string | null;
  sam_email_id: number | null;
  is_provisional: boolean;
  /** Zählt als Anfo (gültig, keine Testorder) — nur dann tragen die Merkmale darunter Werte. */
  is_anfo: boolean;
  invoice_stage: 'keine_rechnung' | 'fakturiert_unbezahlt' | 'nur_testphase' | 'bezahlt' | null;
  customer_type: 'erstbesteller' | 'frueher_gezahlt' | 'frueher_nie_gezahlt' | null;
  prior_anfos: number | null;
  is_serial_nonpayer: boolean;
  is_duplicate: boolean;
  order_page: string | null;
  webinar_id: string | null;
  webinar_title: string | null;
  webinar_date: string | null;
  mailing_campaign_id: number | null;
  mailing_campaign: string | null;
  mailing_number: number | null;
}

export interface PlxOrderListMeta {
  asOf: string;
  mode: 'zeitraum' | 'suche';
  truncated: boolean;
  limit?: number;
  q?: string;
}

const num = (v: unknown): number => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const numOrNull = (v: unknown): number | null =>
  v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
const bool = (v: unknown): boolean => Number(v) === 1 || v === true;
const strOrNull = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

/** DECIMAL kommt von mysql2 als Text, Flags als 0/1 — eine Stelle für beides. */
export const normalizePlxOrderListRow = (r: Record<string, unknown>): PlxOrderListRow => ({
  ordernumber: String(r.ordernumber),
  mandatorid: String(r.mandatorid ?? ''),
  created_on: String(r.created_on ?? ''),
  kunde: numOrNull(r.kunde),
  email: strOrNull(r.email),
  vorname: strOrNull(r.vorname),
  nachname: strOrNull(r.nachname),
  firma: strOrNull(r.firma),
  artikel: strOrNull(r.artikel),
  werbeaktion_wbkz: strOrNull(r.werbeaktion_wbkz),
  vertrag_sts: strOrNull(r.vertrag_sts),
  kuendigungsdatum: strOrNull(r.kuendigungsdatum),
  aboende_datum: strOrNull(r.aboende_datum),
  offerurl: strOrNull(r.offerurl),
  product_name: String(r.product_name ?? 'Unbekannt'),
  is_upsell: bool(r.is_upsell),
  is_one_time: bool(r.is_one_time),
  trial_days: num(r.trial_days),
  trial_end: strOrNull(r.trial_end),
  display_price: num(r.display_price),
  actual_revenue: num(r.actual_revenue),
  paid_revenue_net: numOrNull(r.paid_revenue_net),
  has_actual_data: bool(r.has_actual_data),
  zahlung_brutto: numOrNull(r.zahlung_brutto),
  storno_brutto: num(r.storno_brutto),
  ruecklastschrift_brutto: num(r.ruecklastschrift_brutto),
  rueckueberweisung_brutto: num(r.rueckueberweisung_brutto),
  ausbuchung_brutto: num(r.ausbuchung_brutto),
  subscription_type: strOrNull(r.subscription_type),
  source: String(r.source ?? ''),
  blog: strOrNull(r.blog),
  channel: strOrNull(r.channel),
  channel_label: strOrNull(r.channel_label),
  blog_sektion: strOrNull(r.blog_sektion),
  effective_banner: strOrNull(r.effective_banner),
  sam_email_id: numOrNull(r.sam_email_id),
  is_provisional: bool(r.is_provisional),
  is_anfo: bool(r.is_anfo),
  invoice_stage: strOrNull(r.invoice_stage) as PlxOrderListRow['invoice_stage'],
  customer_type: strOrNull(r.customer_type) as PlxOrderListRow['customer_type'],
  prior_anfos: numOrNull(r.prior_anfos),
  is_serial_nonpayer: bool(r.is_serial_nonpayer),
  is_duplicate: bool(r.is_duplicate),
  order_page: strOrNull(r.order_page),
  webinar_id: strOrNull(r.webinar_id),
  webinar_title: strOrNull(r.webinar_title),
  webinar_date: strOrNull(r.webinar_date),
  mailing_campaign_id: numOrNull(r.mailing_campaign_id),
  mailing_campaign: strOrNull(r.mailing_campaign),
  mailing_number: numOrNull(r.mailing_number),
});

/**
 * Bestellliste v2. Ohne `search`: alle Bestellungen des Zeitraums mit den Seitenfiltern. Mit `search`: Treffer
 * über ALLE Zeiträume (E-Mail, Name, Firma, Bestell-/Kundennummer), nur der Testorder-Schalter gilt.
 */
export const fetchPlxOrderList = async (
  filterState: IFilterState,
  search?: string,
): Promise<{ rows: PlxOrderListRow[]; meta: PlxOrderListMeta }> => {
  const options = search
    ? {
        q: search,
        ...(filterState.custom?.excludeTestOrders !== undefined && {
          excludeTestOrders: filterState.custom.excludeTestOrders,
        }),
      }
    : plxListFilterOptions(filterState);
  const body = (await fetchData('/api/plx-orders/list', options, false)) as {
    success: boolean;
    data?: Record<string, unknown>[];
    meta?: PlxOrderListMeta;
    error?: string;
  };
  if (!body?.success || !Array.isArray(body.data) || !body.meta) {
    throw new Error(body?.error || 'Bestellliste: unerwartete Antwort');
  }
  return { rows: body.data.map(normalizePlxOrderListRow), meta: body.meta };
};

export const fetchPlxOrderStats = async (filterState: IFilterState): Promise<PlxOrderStats> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters for PLX (same as fetchPlxOrders)
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxOrderStats>(
    '/api/plx-orders/stats',
    options,
    false,
  ) as Promise<PlxOrderStats>;
};

export const fetchPlxWerbeaktionConfig = async (): Promise<PlxWerbeaktionConfig[]> => {
  return fetchData<PlxWerbeaktionConfig>('/api/plx-orders/config', {}, true) as Promise<
    PlxWerbeaktionConfig[]
  >;
};

export const savePlxWerbeaktionConfig = async (config: {
  werbeaktion_wbkz: string;
  plx_product: string;
  quarterly_price: number;
  is_upsell: boolean;
  is_one_time: boolean;
  is_cash_upfront: boolean;
  trial_days: number;
}): Promise<{ success: boolean; message: string }> => {
  const response = await fetch('/api/plx-orders/config', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxProducts = async (): Promise<PlxProduct[]> => {
  return fetchData<PlxProduct>('/api/plx-orders/products', {}, true) as Promise<PlxProduct[]>;
};

export const savePlxProduct = async (product: {
  plx_product: string;
  name: string;
  description?: string;
  is_one_time?: boolean;
  default_quarterly_price: number;
}): Promise<{
  success: boolean;
  message: string;
  created?: boolean;
  anfo_wert?: number | null;
  warning?: string | null;
}> => {
  const response = await fetch('/api/plx-orders/products', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(product),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const deletePlxProduct = async (
  plx_product: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`/api/plx-orders/products/${plx_product}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxPriceHistory = async (
  werbeaktion_wbkz: string,
): Promise<PlxWerbeaktionConfigHistory[]> => {
  const response = await fetch(`/api/plx-orders/config/${werbeaktion_wbkz}/history`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const savePlxPriceHistory = async (
  werbeaktion_wbkz: string,
  historyData: {
    plx_product: string;
    quarterly_price: number;
    is_upsell: boolean;
    is_one_time: boolean;
    is_cash_upfront: boolean;
    trial_days: number;
    valid_from: string;
    valid_until?: string;
  },
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`/api/plx-orders/config/${werbeaktion_wbkz}/history`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(historyData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const deletePlxPriceHistory = async (
  werbeaktion_wbkz: string,
  id: number,
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`/api/plx-orders/config/${werbeaktion_wbkz}/history/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const recachePlxOrderMetrics = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await fetch('/api/plx-orders/recache-metrics', {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const recachePlxPaymentEvents = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await fetch('/api/plx-orders/recache-payment-events', {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxFilterOptions = async (): Promise<PlxFilterOptions> => {
  return fetchData<PlxFilterOptions>(
    '/api/plx-orders/filter-options',
    {},
    false,
  ) as Promise<PlxFilterOptions>;
};

export const fetchUnmappedWKZs = async (): Promise<UnmappedWKZ[]> => {
  return fetchData<UnmappedWKZ>('/api/plx-orders/unmapped-wkz', {}, true) as Promise<UnmappedWKZ[]>;
};

export interface StubOrder {
  ordernumber: string;
  kunde: string | null;
  created_on: string;
  banner: string | null;
  offerid: string | null;
  offerurl: string | null;
  vertrag_sts: string | null;
  umsatz_brutto: number | null;
  zahlung_brutto: number | null;
  // 1 = Artikel + WKZ stehen vorlaeufig aus PX-Data drin (plx_px_stub_fills), PL-X hat nicht nachgeliefert.
  provisional: number;
  px_artikel?: string | null;
  px_wkz?: string | null;
  px_zahlart?: string | null;
}

// Antwort in der Huelle { success, data } — hier entpacken (fetchData liefert den rohen Body).
export const fetchStubOrders = async (): Promise<StubOrder[]> => {
  const body = (await fetchData('/api/plx-orders/data-quality/stubs', {}, false)) as
    | { data?: StubOrder[] }
    | StubOrder[];
  return Array.isArray(body) ? body : (body?.data ?? []);
};

// Visualization data interfaces
export interface PlxTimelineData {
  date: string;
  order_count: number;
  revenue: number;
}

/**
 * Antwortform der vier Performance-Endpunkte seit 2026-09-22: Zeilen plus eine im BACKEND
 * gerechnete Summenzeile. Sie muss von dort kommen, weil jede Quote ihren eigenen Nenner hat —
 * das Frontend kann aus den Zeilenwerten kein korrektes Mittel bilden (es kennt nur ein Gewicht
 * je Zeile). Siehe node-app/src/helpers/plxPerformanceMetrics.ts.
 */
export interface PerformanceResponse<T> {
  data: T[];
  summary: T | null;
}

export interface PlxProductPerformance {
  product_name: string;
  artikel: string;
  total_orders: number;
  anteil: number;
  paid_orders: number;
  upsells_count: number;
  upsells_rate: number;
  total_revenue: number;
  conversion_rate: number;
  conversion_rate_quarterly: number;
  conversion_rate_upsells: number;
  churn_rate: number;
  retention_rate: number;
  abo_2_60_days: number;
  abo_4_120_days: number;
  abo_4_120_days_quarterly: number;
  abo_4_120_days_upsell: number;
  avg_lifetime_value: number;
  avg_current_lifetime_days: number;
  expected_revenue: number;
  expected_anfo_value: number;
}

export interface PlxSourcePerformance {
  source: string;
  total_orders: number;
  anteil: number;
  paid_orders: number;
  total_revenue: number;
  conversion_rate: number;
  churn_rate: number;
  retention_rate: number;
  abo_2_60_days: number;
  abo_4_120_days: number;
  abo_4_120_days_quarterly: number;
  abo_4_120_days_upsell: number;
  avg_lifetime_value: number;
  avg_current_lifetime_days: number;
  expected_revenue: number;
  expected_anfo_value: number;
}

export interface PlxWkzPerformance {
  werbeaktion_wbkz: string;
  campaign_type: string;
  total_orders: number;
  anteil: number;
  paid_orders: number;
  total_revenue: number;
  conversion_rate: number;
  churn_rate: number;
  retention_rate: number;
  abo_2_60_days: number;
  abo_4_120_days: number;
  abo_4_120_days_quarterly: number;
  abo_4_120_days_upsell: number;
  avg_lifetime_value: number;
  avg_current_lifetime_days: number;
  expected_revenue: number;
  expected_anfo_value: number;
}

export interface PlxBlogPerformance {
  blog: string;
  total_orders: number;
  anteil: number;
  paid_orders: number;
  total_revenue: number;
  conversion_rate: number;
  churn_rate: number;
  retention_rate: number;
  abo_2_60_days: number;
  abo_4_120_days: number;
  abo_4_120_days_quarterly: number;
  abo_4_120_days_upsell: number;
  avg_lifetime_value: number;
  avg_current_lifetime_days: number;
  expected_revenue: number;
  expected_anfo_value: number;
}

export interface PlxSubscriptionDistribution {
  subscription_type: string;
  count: number;
  revenue: number;
}

export interface PlxRetentionCurve {
  days: number;
  total_eligible: number;
  active_count: number;
  retention_percentage: number;
}

export interface PlxMrrAnalysis {
  month: string;
  subscriber_count: number;
  mrr: number;
  mrr_bezahlt: number;
  mrr_upsell: number;
}

export interface PlxMonthlyRevenue {
  month: string;
  total_revenue: number;
  quarterly_revenue: number;
  annual_revenue: number;
  onetime_revenue: number;
  payment_count: number;
  paying_orders: number;
  quarterly_payments: number;
  annual_payments: number;
  onetime_payments: number;
}

export interface PlxGroupingStats {
  category: string;
  patterns: string[];
  order_count: number;
  examples: string[];
}

export interface PlxGroupingOrder {
  ordernumber: string;
  created_on: string;
  banner: string;
  offerurl: string;
  email: string;
  product_name: string;
}

export interface PlxGroupingOrdersResponse {
  data: PlxGroupingOrder[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// Visualization data fetch functions
export const fetchPlxTimeline = async (
  filterState: IFilterState,
  aggregation = 'daily',
): Promise<PlxTimelineData[]> => {
  const options: any = { aggregation };

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxTimelineData>('/api/plx-orders/timeline', options, true) as Promise<
    PlxTimelineData[]
  >;
};

export const fetchPlxProductPerformance = async (
  filterState: IFilterState,
): Promise<PerformanceResponse<PlxProductPerformance>> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PerformanceResponse<PlxProductPerformance>>(
    '/api/plx-orders/product-performance',
    options,
    false,
  ) as Promise<PerformanceResponse<PlxProductPerformance>>;
};

export const fetchPlxSourcePerformance = async (
  filterState: IFilterState,
): Promise<PerformanceResponse<PlxSourcePerformance>> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PerformanceResponse<PlxSourcePerformance>>(
    '/api/plx-orders/source-performance',
    options,
    false,
  ) as Promise<PerformanceResponse<PlxSourcePerformance>>;
};

export const fetchPlxWkzPerformance = async (
  filterState: IFilterState,
): Promise<PerformanceResponse<PlxWkzPerformance>> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PerformanceResponse<PlxWkzPerformance>>(
    '/api/plx-orders/wkz-performance',
    options,
    false,
  ) as Promise<PerformanceResponse<PlxWkzPerformance>>;
};

export const fetchPlxBlogPerformance = async (
  filterState: IFilterState,
): Promise<PerformanceResponse<PlxBlogPerformance>> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PerformanceResponse<PlxBlogPerformance>>(
    '/api/plx-orders/blog-performance',
    options,
    false,
  ) as Promise<PerformanceResponse<PlxBlogPerformance>>;
};

export const fetchPlxSubscriptionDistribution = async (
  filterState: IFilterState,
): Promise<PlxSubscriptionDistribution[]> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxSubscriptionDistribution>(
    '/api/plx-orders/subscription-distribution',
    options,
    true,
  ) as Promise<PlxSubscriptionDistribution[]>;
};

export const fetchPlxRetentionCurve = async (
  filterState: IFilterState,
): Promise<PlxRetentionCurve[]> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxRetentionCurve>('/api/plx-orders/retention-curve', options, true) as Promise<
    PlxRetentionCurve[]
  >;
};

export const fetchPlxMrrAnalysis = async (filterState: IFilterState): Promise<PlxMrrAnalysis[]> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.artikel = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.vertrag_sts = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.banner) {
    options.banner = filterState.custom.banner;
  }
  if (filterState.custom?.banner_contains) {
    options.banner_contains = filterState.custom.banner_contains;
  }
  if (filterState.custom?.banner_not_contains) {
    options.banner_not_contains = filterState.custom.banner_not_contains;
  }
  if (filterState.custom?.werbeaktion_wbkz) {
    options.werbeaktion_wbkz = filterState.custom.werbeaktion_wbkz;
  }
  if (filterState.custom?.subscription_type) {
    options.subscription_type = filterState.custom.subscription_type;
  }
  if (filterState.custom?.source) {
    options.source = filterState.custom.source;
  }
  if (filterState.custom?.blog) {
    options.blog = filterState.custom.blog;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxMrrAnalysis>('/api/plx-orders/mrr-analysis', options, true) as Promise<
    PlxMrrAnalysis[]
  >;
};

export const fetchPlxMonthlyRevenue = async (
  filterState: IFilterState,
): Promise<PlxMonthlyRevenue[]> => {
  const options: any = {};

  if (filterState.startDate) {
    options.startDate = formatDateForAPI(filterState.startDate);
  }
  if (filterState.endDate) {
    options.endDate = formatDateForAPI(filterState.endDate);
  }

  // Custom filters
  if (filterState.custom?.artikel) {
    options.productFilter = filterState.custom.artikel;
  }
  if (filterState.custom?.vertrag_sts) {
    options.statusFilter = filterState.custom.vertrag_sts;
  }
  if (filterState.custom?.excludeTestOrders !== undefined) {
    options.excludeTestOrders = filterState.custom.excludeTestOrders;
  }

  return fetchData<PlxMonthlyRevenue>('/api/plx-orders/monthly-revenue', options, true) as Promise<
    PlxMonthlyRevenue[]
  >;
};

export const fetchPlxOrderDetails = async (ordernumber: string): Promise<PlxOrderDetails> => {
  const response = await fetch(`/api/plx-orders/${ordernumber}/details`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxSourceGroupingStats = async (): Promise<PlxGroupingStats[]> => {
  const response = await fetch('/api/plx-orders/source-grouping-stats');

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxBlogGroupingStats = async (): Promise<PlxGroupingStats[]> => {
  const response = await fetch('/api/plx-orders/blog-grouping-stats');

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchPlxGroupingOrders = async (
  type: 'source' | 'blog',
  category: string,
  page = 1,
  pageSize = 50,
): Promise<PlxGroupingOrdersResponse> => {
  const response = await fetch(
    `/api/plx-orders/grouping-orders/${type}/${encodeURIComponent(category)}?page=${page}&pageSize=${pageSize}`,
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export interface PlxGroupingOrderExport extends PlxGroupingOrder {
  category: string;
}

export const fetchPlxSourceGroupingExport = async (): Promise<PlxGroupingOrderExport[]> => {
  const response = await fetch(`/api/plx-orders/source-grouping-export`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  return result.data;
};

export const fetchPlxBlogGroupingExport = async (): Promise<PlxGroupingOrderExport[]> => {
  const response = await fetch(`/api/plx-orders/blog-grouping-export`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  return result.data;
};

export interface RevenueVarianceSummary {
  total_theoretical: number;
  total_actual: number;
  total_variance: number;
  variance_percentage: number;
  orders_with_variance: number;
  orders_with_actual_data: number;
  coverage_percentage: number;
}

export interface ProductVariance {
  product_name: string;
  order_count: number;
  theoretical_revenue: string;
  actual_revenue: string;
  variance: string;
  variance_pct: string;
  avg_variance_per_order: string;
}

export interface OrderVariance {
  ordernumber: string;
  product_name: string;
  subscription_type: string;
  theoretical_revenue: string;
  actual_revenue: string;
  variance: string;
  variance_pct: string;
  created_on: string;
  lifetime_days: number;
  has_actual_data: boolean;
}

export interface VarianceDistribution {
  variance_bucket: string;
  order_count: number;
}

export interface RevenueVarianceData {
  summary: RevenueVarianceSummary;
  by_product: ProductVariance[];
  top_variance_orders: OrderVariance[];
  variance_distribution: VarianceDistribution[];
}

export const fetchRevenueVarianceAnalysis = async (
  startDate?: string,
  endDate?: string,
  products?: string[],
  aboStatuses?: string[],
): Promise<RevenueVarianceData> => {
  let url = `/api/plx-orders/revenue-variance-analysis?`;

  if (startDate) url += `startDate=${startDate}&`;
  if (endDate) url += `endDate=${endDate}&`;
  if (products && products.length > 0) url += `products=${products.join(',')}&`;
  if (aboStatuses && aboStatuses.length > 0) url += `aboStatuses=${aboStatuses.join(',')}&`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export interface AnfoValuationRow {
  product_name: string;
  artikel: string;
  total_anfos: number;
  paid_anfos: number;
  conversion_rate: number;
  active_count: number;
  ended_count: number;
  cancelled_count: number;
  /** Testphase laeuft noch — offene Entscheidung, nicht in `unpaid_count` enthalten. */
  trial_count: number;
  unpaid_count: number;
  actual_revenue: number;
  expected_anfo_value: number;
  expected_total_revenue: number;
  revenue_per_anfo: number;
  /** Modell-Gegenprobe (Anfo-Wert M4): Fenster/n/R/G des wirksamen Laufs, NULL ohne Lauf. */
  model_window?: string | null;
  model_n?: number | null;
  model_r?: number | null;
  model_g?: number | null;
  /** Ø Umsatz bis Tag 75 der reifen Anfos (Alter >= 95) dieses Filters. */
  rev75_n?: number;
  rev75_mean?: number | null;
  /** rev75_mean − R: derselbe Massstab, aus dem das Modell R schaetzt. */
  divergence: number | null;
}

/** Summenzeile aus dem Backend — dieselbe Bestellmenge wie die Zeilen, product_name = 'Insgesamt'. */
export interface AnfoValuationResponse {
  rows: AnfoValuationRow[];
  summary: AnfoValuationRow;
  modelRun?: AnfoWertRun | null;
}

export const fetchAnfoValuation = async (
  startDate?: string,
  endDate?: string,
  werbeaktionWbkz?: string[],
  excludeTestOrders?: boolean,
): Promise<AnfoValuationResponse> => {
  let url = `/api/plx-orders/anfo-valuation?`;

  if (startDate) url += `startDate=${startDate}&`;
  if (endDate) url += `endDate=${endDate}&`;
  if (werbeaktionWbkz && werbeaktionWbkz.length > 0)
    url += `werbeaktion_wbkz=${werbeaktionWbkz.join(',')}&`;
  if (excludeTestOrders !== undefined) url += `excludeTestOrders=${excludeTestOrders}&`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const json = await response.json();
  return json.data as AnfoValuationResponse;
};

// Anfo-Wert (Modell M4): Stand des wirksamen Laufs, je Produkt Fenster/n/Werte.
export interface AnfoWertRun {
  id: number;
  calc_month: string;
  as_of: string;
  mode: string;
  status: string;
  cache_status: string;
  finished_at: string | null;
}
export interface AnfoWertHistoryRow {
  product_id: number;
  plx_product: string;
  window_kind: 'current' | 'historisch' | 'pool' | 'onetime' | 'legacy' | null;
  window_start: string | null;
  window_end: string | null;
  n: number | null;
  value_raw: number | null;
  value_smoothed: number | null;
  alert_reason: string | null;
}
export interface AnfoWertParams {
  run: AnfoWertRun | null;
  params: {
    g?: number;
    mu?: number;
    pct?: number;
    oneTime?: { p: number; n: number } | null;
    priorRuns?: number[];
    /** Nur Modell m5: Hochrechnung je Zahlungsart (T Testphase, S Sofortzahlung). */
    m5?: { gCell?: { T: number; S: number } } | null;
  } | null;
  lastRun: AnfoWertRun | null;
  pct: number | null;
  products: AnfoWertHistoryRow[];
  /** Aktives Modell; fehlt (alter Server) = m4. */
  model?: 'm4' | 'm5';
  /** m5: Wert je Angebot (WKZ). Unter m4 leer. */
  offers?: AnfoWertOffer[];
}

export interface AnfoWertOffer {
  werbeaktion_wbkz: string;
  plx_product: string;
  /** 'S' Sofortzahlung (Jahresbezug oder ohne Testphase), 'T' Testphase. */
  zahlungsart: string;
  first_amount: number | null;
  anfo_wert: number;
  anfo_cpa: number | null;
}

export const fetchAnfoWertParams = async (): Promise<AnfoWertParams> => {
  const body = (await fetchData('/api/plx-orders/anfo-wert/params', {}, false)) as {
    data?: AnfoWertParams;
  };
  if (!body?.data) throw new Error('Anfo-Wert-Stand nicht lesbar');
  return body.data;
};

// CPA Percentage Settings
export const fetchCpaPercentage = async (): Promise<number> => {
  const response = await fetch('/api/plx-orders/settings/cpa-percentage');
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  if (typeof data.value !== 'number') throw new Error(data.error || 'CPA-Anteil fehlt');
  return data.value;
};

export const saveCpaPercentage = async (percentage: number): Promise<void> => {
  const response = await fetch('/api/plx-orders/settings/cpa-percentage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ percentage }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
};

// Campaign Attribution types
export interface WebinarAttribution {
  webinar_id: string;
  title: string;
  start_date: string | null;
  mapping_method:
    | 'rid'
    | 'banner_webinar_id'
    | 'email_banner'
    | 'email_doi_url'
    | 'email_affiliate'
    | 'unmapped';
  evaluation_url: string;
}

export interface PlxMailingAttribution {
  campaign_id: number;
  campaign_name: string;
  product_name: string;
  email_id: number;
  mailing_number: number;
  email_subject: string;
  sent_at: string | null;
  evaluation_url: string;
}

export interface CampaignAttribution {
  type: 'webinar' | 'plx_mailing' | 'none';
  webinar?: WebinarAttribution;
  plx_mailing?: PlxMailingAttribution;
}

// Cashflow Forecast types
export interface WeeklyBucket {
  week_label: string;
  week_start: string;
  week_end: string;
  existing_gross: number;
  existing_adjusted: number;
  anfo_gross: number;
  anfo_adjusted: number;
  no_lba_gross: number;
  no_lba_adjusted: number;
  total_gross: number;
  total_adjusted: number;
  order_count: number;
  anfo_count: number;
  no_lba_count: number;
}

export interface ForecastRow {
  ordernumber: string;
  product_name: string;
  artikel: string;
  product_type: 'quartalsabo' | 'upsell';
  layer: 'existing' | 'anfo' | 'no_lba';
  invoice_amount: number;
  mahnstufe: number | null;
  lba_week: string | null;
  expected_payment_date: string;
  probability: number;
  adjusted_amount: number;
}

export interface ForecastSummary {
  total_forecast_gross: number;
  total_forecast_adjusted: number;
  active_subscriptions: number;
  active_upsells: number;
  upsell_trials: number;
  with_lba: number;
  without_lba: number;
  active_trials: number;
  dunning_distribution: {
    level: number;
    count: number;
    total_amount: number;
  }[];
  product_breakdown: {
    product: string;
    count: number;
    gross: number;
    adjusted: number;
  }[];
}

export interface CashflowForecastData {
  weeks: WeeklyBucket[];
  summary: ForecastSummary;
  details: ForecastRow[];
}

export const fetchCashflowForecast = async (
  productTypes?: string[],
): Promise<CashflowForecastData> => {
  const params = productTypes?.length ? `?productTypes=${productTypes.join(',')}` : '';
  const response = await fetch(`/api/plx-orders/cashflow-forecast${params}`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Cashflow Rückblick / Plan-vs-Ist types
export interface CashflowHistoryWeek {
  iso_year_week: string;
  week_label: string;
  week_start: string;
  is_future: boolean;
  is_partial: boolean;
  has_gap: boolean;
  no_data: boolean;
  quartalsabo_gross: number;
  upsell_gross: number;
  chargebacks: number;
  ist_net: number;
  plan_gross: number;
  plan_adjusted: number;
}

export interface CashflowHistorySummary {
  completed_weeks: number;
  cum_ist_net: number;
  cum_plan_gross: number;
  cum_plan_adjusted: number;
  cum_chargebacks: number;
  drift_pct: number | null;
}

export interface QuarterOption {
  value: string;
  label: string;
  baseline_complete: boolean;
}

export interface CashflowHistoryData {
  quarter: string;
  baseline_date: string | null;
  baseline_complete: boolean;
  snapshots_started: string | null;
  weeks: CashflowHistoryWeek[];
  summary: CashflowHistorySummary;
  quarters_available: QuarterOption[];
}

export const fetchCashflowHistory = async (quarter?: string): Promise<CashflowHistoryData> => {
  const params = quarter ? `?quarter=${encodeURIComponent(quarter)}` : '';
  const response = await fetch(`/api/plx-orders/cashflow-history${params}`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const fetchCampaignAttribution = async (
  ordernumber: string,
): Promise<CampaignAttribution> => {
  const response = await fetch(`/api/plx-orders/${ordernumber}/campaign-attribution`);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// --- Kündigungsquoten (Kohorten-basiert, Meilenstein-Semantik) ---

export interface KqCohort {
  month: string;
  anfos_total: number;
  anfos_known_price: number;
  unknown_price: number;
  current_fallback_count: number;
  derived_lifetime_count: number;
  paid_1: number;
  paid_2: number;
  max_due_1: string | null;
  max_due_2: string | null;
  mature_1: boolean;
  mature_2: boolean;
  kq1: number | null;
  kq2_besteller: number | null;
  kq2_zahler: number | null;
}

export interface KqRolling {
  window: number;
  kq1: number | null;
  kq1_delta: number | null;
  kq1_months: string[];
  kq2_besteller: number | null;
  kq2_besteller_delta: number | null;
  kq2_zahler: number | null;
  kq2_zahler_delta: number | null;
  kq2_months: string[];
}

export type KqMode = 'amount' | 'date';

export interface KqResponse {
  cohorts: KqCohort[];
  rolling: KqRolling;
  meta: {
    grace_days: number;
    window: number;
    today: string;
    mode: KqMode;
    count_partial: boolean;
    wkz_without_history: number;
  };
}

export const fetchKuendigungsquoten = async (
  artikel?: string[],
  excludeTestOrders?: boolean,
  windowSize?: number,
  mode?: KqMode,
  countPartial?: boolean,
): Promise<KqResponse> => {
  let url = '/api/plx-orders/kuendigungsquoten?';
  if (artikel && artikel.length > 0) url += `artikel=${encodeURIComponent(artikel.join(','))}&`;
  if (excludeTestOrders !== undefined) url += `excludeTestOrders=${excludeTestOrders}&`;
  if (windowSize) url += `window=${windowSize}&`;
  if (mode) url += `mode=${mode}&`;
  if (mode === 'date' && countPartial !== undefined) url += `countPartial=${countPartial}&`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const body = await response.json();
  if (!body.success) {
    throw new Error(body.error || 'Unbekannter Fehler');
  }
  return body.data as KqResponse;
};

export interface KqStreitfall {
  ordernumber: string;
  month: string;
  vertrag_sts: string | null;
  zahlung: number;
  netto_faktura: number;
  price: number | null;
  aboende: string | null;
  kuendigung: string | null;
  cap: string | null;
  paid_a: [number, number] | null;
  paid_b_strict: [number, number] | null;
  paid_b_partial: [number, number] | null;
  mature_1: boolean;
  mature_2: boolean;
  flags: {
    preis_unbekannt: boolean;
    ohne_cap: boolean;
    anteilig_verdacht: boolean;
    betrag_vs_laufzeit: boolean;
    zahlt_ohne_faktura: boolean;
    zahlung_ueber_faktura: boolean;
  };
}

export interface KqStreitfaelleResponse {
  data: KqStreitfall[];
  totals: {
    rows_total: number;
    preis_unbekannt: number;
    ohne_cap: number;
    anteilig_verdacht: number;
    betrag_vs_laufzeit: number;
    zahlt_ohne_faktura: number;
    zahlung_ueber_faktura: number;
  };
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export const fetchKqStreitfaelle = async (
  artikel?: string[],
  excludeTestOrders?: boolean,
  page?: number,
  pageSize?: number,
): Promise<KqStreitfaelleResponse> => {
  let url = '/api/plx-orders/kuendigungsquoten/streitfaelle?';
  if (artikel && artikel.length > 0) url += `artikel=${encodeURIComponent(artikel.join(','))}&`;
  if (excludeTestOrders !== undefined) url += `excludeTestOrders=${excludeTestOrders}&`;
  if (page) url += `page=${page}&`;
  if (pageSize) url += `pageSize=${pageSize}&`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const body = await response.json();
  if (!body.success) {
    throw new Error(body.error || 'Unbekannter Fehler');
  }
  return body as KqStreitfaelleResponse;
};
